import React, { useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FaSave, FaTimes, FaPlus, FaSearch, FaFileExport, FaEraser, FaCheck, FaMicrophone } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { Table, Button, Modal, Spinner } from "react-bootstrap";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import { printAdmissionA4 } from "./Pages/AdmissionPrint";
// Import modular components
import PatientDetails from "./Pages/PatientDetails";
import AdmissionDetails from "./Pages/AdmissionDetails";
import VitalSigns from "./Pages/VitalSigns";
import GuardianInformation from "./Pages/GuardianInformation";
import SymptomsDiagnosis from "./Pages/SymptomsDiagnosis";
import InsurancePayment from "./Pages/InsurancePayment";
import AddPatientModal from "./Pages/AddPatientModal"; // ✅ Use AddPatientModal
import Print from "./Pages/Print";
import styles from "./NewIPDPatientAdmission.module.css";
import AuthContext from "../../../store/auth-context";

const NewIPDPatientAdmission = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const hospitalData = authCtx.hospitalData;
  const navigate = useNavigate();
  const initialFormData = {
    ipdAdmissionId: 0,
    patientId: {
      patientId: 0,
      firstName: "",
      middleName: "",
      lastName: "",
      dob: "",
      maritalStatus: "",
      religion: "",
      caste: "",
      panCardNo: "",
      adharCardNo: "",
      gender: "",
      age: 0,
      caseNo: "",
      occupation: "",
      identificationMark: "",
      emailId: "",
      mobile: "",
      alternateMobileNum: "",
      address: "",
      city: "",
      pincode: "",
      referredBy: "",
      nickName: "",
      registrationNo: ""
    },
    admissionDateTime: "",
    dischargeDateTime: null,
    caseNo: "",
    wardBed: {
      id: 0,
      bedNumber: 0,
      floor: "",
      wardName: "",
      description: "",
      note: ""
    },
    // Vital Signs (Restored)
    height: "",
    weight: "",
    bp: "",
    bp_systolic: "",
    bp_diastolic: "",
    pulse: "",
    temperature: "",
    spo2: "",
    bloodGroup: "",
    doctorId: {
      userId: 0,
      firstName: "",
      middleName: "",
      lastName: "",
      mobile: "",
      email: "",
      userName: "",
      degree: "",
      role: ""
    },
    admissionReason: "",
    diagnosis: "",
    guardianName: "",
    guardianRelation: "",
    guardianContactNumber: "",
    symptomsTitle: "",
    symptomsDesc: "",
    tpaId: "",
    tpaValidity: "",
    adharCardNo: "",
    panCardNo: "",
    insuranceCardNo: "",
    bankAccount: "",
    notes: "",
    nextFollowupVisitToDr: "",
    status: "ACTIVE",
    option2: "",
    option3: "",
    option4: "",
    option5: "",
    pho: "",
    co: "",
    kco: "",
    cf: "",
    referredBy: ""
  };
  const [formData, setFormData] = useState(initialFormData);

  const [searchPara, setSearchPara] = useState("");
  const [patientData, setPatientData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const notesRef = useRef(null);

  // State for dynamic dropdowns
  const [doctorsList, setDoctorsList] = useState([]);
  const [availableBeds, setAvailableBeds] = useState([]);

  // Helper to get local ISO string (YYYY-MM-DDTHH:mm)
  const getLocalTime = () => {
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  // Set default admission date/time and fetch dynamic data on load
  React.useEffect(() => {
    setFormData(prev => ({
      ...prev,
      admissionDateTime: getLocalTime()
    }));

    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    const apiChar = getApiChara();

    // Fetch Doctors
    const fetchDoctors = async () => {
      try {
        const response = await fetch(`${apiChar}/users/getDoctorsList`, {
          headers: { Authorization: "Bearer " + token },
        });
        if (response.ok) {
          const data = await response.json();
          setDoctorsList(data || []);
        }
      } catch (err) {
        console.error("Error fetching doctors:", err);
      }
    };

    // Fetch Available Beds
    const fetchBeds = async () => {
      try {
        const response = await fetch(`${apiChar}/ipd/wardBed/available`, {
          headers: { Authorization: "Bearer " + token },
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableBeds(data || []);
        }
      } catch (err) {
        console.error("Error fetching beds:", err);
      }
    };

    fetchDoctors();
    fetchBeds();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle nested updates (e.g. "wardBed.floor")
    if (name.includes('.')) {
      const [parent, child] = name.split('.');

      // Special Handling for Object-level select (e.g. "doctorId.userId" or "wardBed.id")
      if (child === "userId" && parent === "doctorId") {
        const selectedDr = doctorsList.find(dr => dr.userId === Number(value));
        setFormData(prev => ({ ...prev, doctorId: selectedDr || initialFormData.doctorId }));
      } else if (child === "id" && parent === "wardBed") {
        const selectedBed = availableBeds.find(bed => bed.id === Number(value));
        if (selectedBed) {
          setFormData(prev => ({ ...prev, wardBed: selectedBed }));
        } else {
          // If "Select Bed" (empty/0) is chosen, keep floor/ward but reset ID/BedNumber
          setFormData(prev => ({
            ...prev,
            wardBed: { ...prev.wardBed, id: 0, bedNumber: 0 }
          }));
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Scroll to notes if status is set to TRANSFERRED or CANCELLED
      if (name === "status" && (value === "TRANSFERRED" || value === "CANCELLED")) {
        setTimeout(() => {
          notesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
          notesRef.current?.focus();
        }, 100);
      }
    }
  };

  const handleVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error(t("Speech Recognition API is not supported in this browser. Please use Chrome."));
      return;
    }

    if (isListening) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      toast.info(t("Listening... Speak now."));
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (transcript) {
        setFormData(prev => ({
          ...prev,
          notes: prev.notes ? prev.notes + " " + transcript : transcript
        }));
        toast.success(t("Voice input added!"));
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      console.error(event.error);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.start();
  };


  const handleSave = async () => {
    // 1. Validate Mandatory Fields
    // patient info, admission date and time , ward bed info, status
    console.log(formData, "formData");
    if (!formData.patientId?.patientId) {
      toast.error("Please select a patient.");
      return;
    }
    if (!formData.admissionDateTime) {
      toast.error("Admission Date & Time is required.");
      return;
    }
    const wardBedId = Number(formData.wardBed?.id || 0);
    if (!formData.wardBed?.floor || !formData.wardBed?.wardName || wardBedId === 0) {
      toast.error("Please select a specific Floor, Ward, and Bed Number.");
      return;
    }
    if (!formData.status) {
      toast.error("Status is required");
      return;
    }
    if (!formData.doctorId?.userId) {
      toast.error("Please select a Consultant Doctor.");
      return;
    }
    if (!formData.admissionReason) {
      toast.error("Admission Reason is required.");
      return;
    }

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/admissions`;
      const token = getAuthToken();

      const formatDateTime = (val) => {
        if (!val) return null;
        // If it's a date-only (YYYY-MM-DD), add time
        if (val.length === 10) return `${val} 00:00:00`;
        // If it's datetime-local (YYYY-MM-DDTHH:mm), replace T with space and add seconds
        if (val.length === 16) return val.replace("T", " ") + ":00";

        // If it's a full ISO string (YYYY-MM-DDTHH:mm:ss.SSSZ)
        // 1. Replace 'T' with ' '
        // 2. Remove anything from '.' onwards (milliseconds/Z)
        return val.replace("T", " ").split(".")[0].replace("Z", "");
      };

      const { bp_systolic, bp_diastolic, ...filteredFormData } = formData;
      const combinedBP = (bp_systolic || bp_diastolic) ? `${bp_systolic || "0"}/${bp_diastolic || "0"}` : "";

      const payload = {
        ...filteredFormData,
        bp: combinedBP,
        admissionDateTime: formatDateTime(formData.admissionDateTime),
        dischargeDateTime: formatDateTime(formData.dischargeDateTime),
        tpaValidity: formData.tpaValidity || null,
      };

      console.log("Mapped Payload for Backend:", payload);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const resText = await response.text();
        let errorMsg = resText || "Failed to save admission";

        try {
          const resData = JSON.parse(resText);
          errorMsg = resData.message || resData.errorMsg || errorMsg;
        } catch (e) {
          // If not JSON, check for specific backend string
          if (resText.includes("Patient already Admitted")) {
            toast.error("Patient already available");
            setIsLoading(false);
            return;
          }
        }
        throw new Error(errorMsg);
      }

      const savedAdmission = await response.json();
      toast.success(`Patient admitted successfully! ID: ${savedAdmission.ipdAdmissionId}`);
      handleClear();
      navigate("/dashboard/ipd");

    } catch (error) {
      console.error(error);
      if (error.message !== "Patient already available") {
        toast.error("Error saving admission: " + error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Search Patient
  const handleSearchChange = (e) => setSearchPara(e.target.value);

  const onSearchPatientHandler = async (e) => {
    e.preventDefault();
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=${searchPara}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient data");

      const data = await response.json();
      if (!data || data.length === 0) toast.info("No patients found");
      setPatientData(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Error fetching patients");
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return 0;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const onPatientSelect = (patient) => {
    setFormData((prev) => ({
      ...prev,
      patientId: {
        ...prev.patientId,
        patientId: patient.patientId || 0,
        firstName: patient.firstName || "",
        middleName: patient.middleName || "",
        lastName: patient.lastName || "",
        dob: patient.dob || "",
        gender: patient.gender || "",
        mobile: patient.mobile || "",
        address: patient.address || "",
        age: patient.age || calculateAge(patient.dob) || 0,
        maritalStatus: patient.maritalStatus || "",
        emailId: patient.email || "",
        registrationNo: patient.registrationNo || "",
        referredBy: patient.referredBy || "",
      },
      bloodGroup: patient.bloodGroup || prev.bloodGroup,
      referredBy: patient.referredBy || ""
    }));

    setPatientData([]); // close selection modal
  };

  // Callback when patient is added in AddPatientModal
  const handlePatientAdded = (newPatient) => {
    setFormData((prev) => ({
      ...prev,
      patientId: {
        ...prev.patientId,
        patientId: newPatient.patientId || 0,
        firstName: newPatient.firstName || "",
        middleName: newPatient.middleName || "",
        lastName: newPatient.lastName || "",
        dob: newPatient.dob || "",
        gender: newPatient.gender || "",
        mobile: newPatient.mobile || "",
        address: newPatient.address || "",
        age: calculateAge(newPatient.dob) || newPatient.age || 0,
        registrationNo: newPatient.registrationNo || "",
        referredBy: newPatient.referredBy || "",
      },
      bloodGroup: newPatient.bloodGroup || prev.bloodGroup,
      referredBy: newPatient.referredBy || ""
    }));
  };
  const handleClear = () => {
    setFormData({
      ...initialFormData,
      admissionDateTime: getLocalTime()
    });
    setSearchPara("");            // clear search bar too if needed
    setPatientData([]);           // clear patient list if showing
  };
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        {/* Left: Title */}
        <h2 className={styles.title}>{t("IPD Patient Admission")}</h2>

        {/* Center: Search bar */}
        <div className={styles.searchWrapper}>
          <form onSubmit={onSearchPatientHandler} className={styles.searchForm}>
            <input
              type="text"
              className="form-control"
              placeholder={t("Search Patients")}
              value={searchPara}
              onChange={handleSearchChange}
              required
            />
            <button
              type="submit"
              className="bg-light text-teal border-0 fw-bold px-3 py-1 d-flex align-items-center justify-content-center"
              style={{ borderRadius: '0 4px 4px 0', height: '38px', minWidth: '45px' }}
              title={t("Search")}
            >
              <FaSearch />
            </button>
          </form>
        </div>

        {/* Right: Add New Patient Button */}
        <button
          className="bg-light text-teal border-0 fw-bold px-4 py-1 d-flex align-items-center gap-2"
          onClick={() => setShowAddPatientModal(true)}
          style={{ borderRadius: '4px', height: '38px', fontSize: '0.85rem' }}
        >
          <FaPlus /> <span className="d-none d-md-inline">{t("ADD NEW PATIENT")}</span><span className="d-md-none">{t("ADD")}</span>
        </button>
      </div>

      {/* Modal for patient selection */}
      <Modal
        show={patientData?.length > 0}
        onHide={() => setPatientData([])}
        centered
        size="lg"
        className="patientModel"
      >
        <Modal.Header closeButton style={{ backgroundColor: 'var(--primary-color)', color: 'white' }}>
          <Modal.Title className="w-100 text-center text-white" style={{ color: 'white' }}>{t("Select Patient")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>{t("Patient Id")}</th>
                <th>{t("Name")}</th>
                <th>{t("Mobile")}</th>
                <th>{t("Action")}</th>
              </tr>
            </thead>
            <tbody>
              {patientData.map((p) => (
                <tr key={p.patientId}>
                  <td>{p.patientId}</td>
                  <td>
                    {[p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ")}
                  </td>
                  <td>{p.mobile || "-"}</td>
                  <td>
                    <Button
                      className="btn-select-premium"
                      onClick={() => onPatientSelect(p)}
                    >
                      <FaCheck className="me-1" /> {t("Select")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      <AddPatientModal
        show={showAddPatientModal}
        onHide={() => setShowAddPatientModal(false)}
        onPatientAdded={handlePatientAdded}   // ✅ new prop
      />

      <div className={styles.formWrapper}>
        {/* Left Panel */}
        <PatientDetails formData={formData} />

        {/* Right / Main Panel */}
        <div className={styles.mainForm}>
          {/* Modular Components */}
          <AdmissionDetails
            formData={formData}
            handleInputChange={handleInputChange}
            doctorsList={doctorsList}
            availableBeds={availableBeds}
          />

          <VitalSigns
            formData={formData}
            handleInputChange={handleInputChange}
          />

          <GuardianInformation
            formData={formData}
            handleInputChange={handleInputChange}
          />

          <SymptomsDiagnosis
            formData={formData}
            handleInputChange={handleInputChange}
          />

          <InsurancePayment
            formData={formData}
            handleInputChange={handleInputChange}
          />

          {/* Remarks */}
          <div className={styles.section}>
            <div className="d-flex align-items-center gap-2 mb-2">
              <h3 className="m-0" style={{ fontSize: '1rem', color: 'var(--primary-color)', fontWeight: '600' }}>
                {t("Admission Notes / Remarks")}
              </h3>
              <FaMicrophone
                onClick={handleVoiceInput}
                style={{
                  cursor: "pointer",
                  fontSize: "1.1rem",
                  color: isListening ? "#f44336" : "#25d366",
                  animation: isListening ? "pulse-mic 1s infinite" : "none"
                }}
                title={t("Voice Input")}
              />
            </div>
            <style>{`
              @keyframes pulse-mic {
                0% { transform: scale(1); }
                50% { transform: scale(1.2); }
                100% { transform: scale(1); }
              }
            `}</style>
            <textarea
              ref={notesRef}
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              placeholder={t("Remarks / Additional Notes (Please specify reason for Transfer/Cancellation if applicable)")}
            />
          </div>

          {/* Actions */}
          <div class={styles.actions}>
            <Print formData={formData} hospitalData={hospitalData} />
            {/* Export Section */}
            <button
              type="button"
              className="btn-export-premium"
              onClick={() =>
                printAdmissionA4(formData, authCtx)
              }
            >
              <FaFileExport className="me-2" /> {t("Export")}
            </button>

            {/* Save / Clear */}
            <button onClick={handleSave} disabled={isLoading} className="btn-premium">
              {isLoading ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <FaSave className="me-2" />
              )}
              {isLoading ? t("Saving...") : t("Save")}
            </button>
            <button onClick={handleClear} className="btn-secondary-premium">
              <FaEraser className="me-2" /> {t("Clear")}
            </button>
          </div>

        </div>
      </div>

    </div>
  );
};

export default NewIPDPatientAdmission;
